import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
  CartesianGrid,
  ComposedChart,
  Line,
} from "recharts";
import {
  Download,
  Trophy,
  Flame,
  CalendarDays,
  StickyNote,
  FolderKanban,
  Users,
  TrendingUp,
  TrendingDown,
  Sparkles,
  AlertTriangle,
  Clock,
  ArrowRight,
  Building2,
} from "lucide-react";
import { api } from "../lib/api";
import { isAssignedToUser } from "../lib/taskPermissions";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuthStore } from "../store/authStore";
import { useViewScopeStore } from "../store/viewScopeStore";
import { useMotivationStore } from "../store/motivationStore";
import { formatInTz } from "../lib/dates";
import MyResponsibilitiesCard from "../components/dashboard/MyResponsibilitiesCard";
import MyPlannedActivitiesCard from "../components/dashboard/MyPlannedActivitiesCard";
import SmartsheetSyncCard from "../components/dashboard/SmartsheetSyncCard";
import UpcomingDeadlinesCard from "../components/dashboard/UpcomingDeadlinesCard";
import PushEnableBanner from "../components/dashboard/PushEnableBanner";

const PRIORITY_COLORS = {
  low: "#a1a1aa",
  medium: "#3b82f6",
  high: "#f59e0b",
  urgent: "#ef4444",
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, tenant } = useAuthStore();
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const isSuperAdmin = ["super_admin", "ultra_admin"].includes(user?.role);
  const { stats: motiv, level, fetchMe: fetchMotivation } = useMotivationStore();

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyGoal, setCompanyGoal] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const jobs = [
          api.get("/tasks", { params: { limit: 1000 } }),
          api.get("/projects"),
          api.get("/motivation/company-goal"),
          api.get("/motivation/leaderboard"),
        ];
        if (isSuperAdmin) jobs.push(api.get("/users"));
        const results = await Promise.allSettled(jobs);
        const [tRes, pRes, cgRes, lbRes, uRes] = results;
        if (tRes.status === "fulfilled") {
          setTasks(
            Array.isArray(tRes.value.data)
              ? tRes.value.data
              : tRes.value.data.items || [],
          );
        }
        if (pRes.status === "fulfilled") setProjects(pRes.value.data || []);
        if (cgRes.status === "fulfilled") setCompanyGoal(cgRes.value.data);
        if (lbRes.status === "fulfilled") setLeaderboard(lbRes.value.data || []);
        if (uRes && uRes.status === "fulfilled") setUsers(uRes.value.data || []);
      } finally {
        setLoaded(true);
      }
    })();
    fetchMotivation();
  }, [isSuperAdmin, fetchMotivation]);

  // Scope-adjusted tasks (Ben/Herkes).
  const scopedTasks = useMemo(() => {
    if (!isMe || !user?.id) return tasks;
    return tasks.filter((tk) => isAssignedToUser(tk, user.id));
  }, [tasks, isMe, user?.id]);

  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6); // last 7 days incl today

  const stats = useMemo(() => {
    const open = scopedTasks.filter((tk) => tk.status !== "done").length;
    const overdue = scopedTasks.filter(
      (tk) =>
        tk.due_at &&
        tk.status !== "done" &&
        new Date(tk.due_at).getTime() < now,
    ).length;
    const dueToday = scopedTasks.filter((tk) => {
      if (!tk.due_at || tk.status === "done") return false;
      const d = new Date(tk.due_at);
      return d >= startOfToday && d < new Date(startOfToday.getTime() + 86400000);
    }).length;
    const doneThisWeek = scopedTasks.filter((tk) => {
      if (tk.status !== "done" || !tk.updated_at) return false;
      return new Date(tk.updated_at).getTime() >= startOfWeek.getTime();
    }).length;

    const byStatus = ["todo", "in_progress", "done"].map((s) => ({
      name: t(`tasks.status_${s}`),
      value: scopedTasks.filter((x) => x.status === s).length,
    }));

    const byPriority = ["low", "medium", "high", "urgent"].map((p) => ({
      name: t(`tasks.priority_${p}`),
      priority: p,
      value: scopedTasks.filter((x) => x.priority === p).length,
    }));

    // 14-day completion trend
    const trend = [];
    for (let i = 13; i >= 0; i--) {
      const day = new Date(startOfToday);
      day.setDate(day.getDate() - i);
      const next = new Date(day);
      next.setDate(next.getDate() + 1);
      const label = day.toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
      });
      const count = scopedTasks.filter((tk) => {
        if (tk.status !== "done" || !tk.updated_at) return false;
        const t2 = new Date(tk.updated_at).getTime();
        return t2 >= day.getTime() && t2 < next.getTime();
      }).length;
      trend.push({ day: label, done: count });
    }

    // 14-day burndown — how many tasks were still open at the end of
    // each day, alongside how many the team completed on that day. A
    // task counts as "open at end-of-day D" if it was created ≤ D AND
    // (still not done, OR completed AFTER D). We approximate the
    // creation/completion timestamps from `created_at` and `updated_at`
    // (the latter is the ground truth for status transitions since a
    // Kanban move rewrites it). Includes an ideal linear reference so
    // the reader can eyeball whether the team is ahead of or behind
    // pace.
    const burndown = [];
    const dayCount = 14;
    // Compute the initial open backlog at start of window as a baseline
    // (open tasks whose created_at ≤ window start; if done, completion > window start).
    const windowStart = new Date(startOfToday);
    windowStart.setDate(windowStart.getDate() - (dayCount - 1));
    const initialOpen = scopedTasks.filter((tk) => {
      const created = tk.created_at ? new Date(tk.created_at).getTime() : 0;
      if (created > windowStart.getTime()) return false;
      if (tk.status !== "done") return true;
      const doneAt = tk.updated_at ? new Date(tk.updated_at).getTime() : 0;
      return doneAt >= windowStart.getTime();
    }).length;
    for (let i = 0; i < dayCount; i++) {
      const day = new Date(windowStart);
      day.setDate(day.getDate() + i);
      const eod = new Date(day);
      eod.setHours(23, 59, 59, 999);
      const eodMs = eod.getTime();
      // open = created ≤ EOD AND (status !== 'done' OR completed > EOD)
      let openCount = 0;
      let doneToday = 0;
      const dayStartMs = day.getTime();
      scopedTasks.forEach((tk) => {
        const created = tk.created_at
          ? new Date(tk.created_at).getTime()
          : 0;
        if (created > eodMs) return;
        const isDone = tk.status === "done";
        const doneAt = tk.updated_at
          ? new Date(tk.updated_at).getTime()
          : null;
        if (!isDone || (doneAt && doneAt > eodMs)) {
          openCount += 1;
        }
        if (isDone && doneAt && doneAt >= dayStartMs && doneAt <= eodMs) {
          doneToday += 1;
        }
      });
      const label = day.toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
      });
      const ideal = Math.max(
        0,
        Math.round(initialOpen - (initialOpen / (dayCount - 1)) * i),
      );
      burndown.push({ day: label, open: openCount, done: doneToday, ideal });
    }

    // By project (top 8 for scoped view)
    const projMap = {};
    scopedTasks.forEach((tk) => {
      if (tk.status === "done") return;
      projMap[tk.project_id] = (projMap[tk.project_id] || 0) + 1;
    });
    const byProject = projects
      .map((p) => ({ name: p.name, open: projMap[p.id] || 0, color: p.color }))
      .filter((p) => p.open > 0)
      .sort((a, b) => b.open - a.open)
      .slice(0, 8);

    return {
      total: scopedTasks.length,
      open,
      overdue,
      dueToday,
      doneThisWeek,
      byStatus,
      byPriority,
      trend,
      byProject,
      burndown,
    };
  }, [scopedTasks, projects, t]);

  const upcoming = useMemo(() => {
    return [...scopedTasks]
      .filter((tk) => tk.status !== "done" && tk.due_at)
      .sort(
        (a, b) =>
          new Date(a.due_at).getTime() - new Date(b.due_at).getTime(),
      )
      .slice(0, 6);
  }, [scopedTasks]);

  const activeUserCount = users.filter((u) => u.active).length;

  const csvExport = () => {
    const rows = [
      ["id", "title", "status", "priority", "assignee_id", "due_at", "project_id"],
      ...scopedTasks.map((tk) => [
        tk.id,
        tk.title,
        tk.status,
        tk.priority,
        tk.assignee_id || "",
        tk.due_at || "",
        tk.project_id,
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "serpix-tasks.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const usersCsvExport = () => {
    if (!users.length) return;
    const rows = [
      [
        "id",
        "name",
        "email",
        "role",
        "active",
        "title",
        "phone",
        "preferred_language",
        "created_at",
      ],
      ...users.map((u) => [
        u.id,
        u.name || "",
        u.email || "",
        u.role || "",
        u.active ? "true" : "false",
        u.title || "",
        u.phone || "",
        u.preferred_language || "",
        u.created_at || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "serpix-users.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Push notification onboarding — only renders if unsubscribed
          and not snoozed. Auto-hides once user enables. */}
      <PushEnableBanner />
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("dashboard.welcome")}, {user?.name}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("nav.dashboard")}
          </h1>
          <div className="mt-1 text-xs text-zinc-500">
            {tenant?.timezone} ·{" "}
            {formatInTz(
              new Date().toISOString(),
              tenant?.timezone,
              "DD MMM YYYY, HH:mm",
            )}{" "}
            ·{" "}
            {isMe
              ? t("dashboard.scope_me")
              : isSuperAdmin
                ? t("dashboard.scope_company")
                : t("dashboard.scope_team")}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" />
      </div>

      {/* Quick shortcut links */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <QuickLink
          to="/app/projects"
          icon={FolderKanban}
          label={t("dashboard.qlink_projects")}
          count={projects.length}
          tone="blue"
          testid="qlink-projects"
        />
        <QuickLink
          to="/app/takvim"
          icon={CalendarDays}
          label={t("dashboard.qlink_calendar")}
          count={stats.dueToday}
          countSuffix={t("dashboard.today_suffix")}
          tone="emerald"
          testid="qlink-takvim"
        />
        <QuickLink
          to="/app/quick-notes"
          icon={StickyNote}
          label={t("dashboard.qlink_notes")}
          tone="amber"
          testid="qlink-notes"
        />
        <QuickLink
          to="/app/motivasyon"
          icon={Trophy}
          label={t("dashboard.qlink_motivation")}
          count={motiv?.xp || 0}
          countSuffix="XP"
          tone="purple"
          testid="qlink-motivasyon"
        />
      </div>

      {/* Top stats — each card links to /app/tasks (the tenant-wide task
          hub) with a hash filter so the user can drill into the underlying
          list, grouped by date. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          testId={TEST_IDS.dashboardTotalTasks}
          label={t("dashboard.stat_total_tasks")}
          value={stats.total}
          icon={<FolderKanban className="h-5 w-5 text-blue-500" />}
          to="/app/tasks#filter=all"
        />
        <StatCard
          testId={TEST_IDS.dashboardOpenTasks}
          label={t("dashboard.stat_open_tasks")}
          value={stats.open}
          icon={<Clock className="h-5 w-5 text-amber-500" />}
          to="/app/tasks#filter=open"
        />
        <StatCard
          testId={TEST_IDS.dashboardOverdue}
          label={t("dashboard.stat_overdue")}
          value={stats.overdue}
          icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
          highlight={stats.overdue > 0}
          to="/app/tasks#filter=overdue"
        />
        <StatCard
          testId="dashboard-done-this-week"
          label={t("dashboard.stat_done_this_week")}
          value={stats.doneThisWeek}
          icon={<TrendingUp className="h-5 w-5 text-emerald-500" />}
          to="/app/tasks#filter=done_this_week"
        />
      </div>

      {/* Personal construction responsibilities — only surfaces when
          the user is on at least one construction row / milestone. Hidden
          automatically otherwise so the dashboard stays uncluttered for
          users who don't touch the Construction module. */}
      <MyResponsibilitiesCard />

      {/* EXPO: countdown for template task due dates in fair-linked
          projects. Auto-hides when the tenant lacks the expo flag or
          there is nothing due within the window. */}
      <UpcomingDeadlinesCard />

      {/* CRM-style planned activities — 4 scope-cards. Each auto-hides
          when empty so users who don't own any activities see nothing.
          Header on each card links to /app/my-activities with the scope
          pre-selected via the ?scope= URL param. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MyPlannedActivitiesCard scope="upcoming" limit={5} />
        <MyPlannedActivitiesCard scope="assigned_to_me" limit={5} />
        <MyPlannedActivitiesCard scope="all" limit={5} />
        <MyPlannedActivitiesCard scope="delegated" limit={5} />
      </div>

      {/* Smartsheet sync — super_admin only, self-hides when the token
          isn't configured or no sheet is bound. */}
      <SmartsheetSyncCard />

      {/* Motivation strip — always visible */}
      {motiv && level && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-700">
              <Trophy className="h-4 w-4" /> {t("dashboard.motiv_level")}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display text-2xl font-semibold text-zinc-900">
                {level.level}
              </span>
              <span className="text-xs text-zinc-500">{motiv.xp} XP</span>
            </div>
            {level.next_level && (
              <>
                <Progress
                  value={Math.min(
                    100,
                    ((motiv.xp - level.level_min) /
                      Math.max(1, level.next_min - level.level_min)) *
                      100,
                  )}
                  className="mt-2 h-1.5 bg-amber-100"
                />
                <div className="mt-1 text-[10px] text-zinc-500">
                  {t("dashboard.motiv_next")}: <b>{level.next_level}</b>
                </div>
              </>
            )}
          </div>
          <div className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-white p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-red-700">
              <Flame className="h-4 w-4" /> {t("dashboard.motiv_streak")}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display text-3xl font-semibold text-red-600">
                {motiv.streak_days}
              </span>
              <span className="text-sm text-zinc-500">{t("dashboard.motiv_day")}</span>
            </div>
            <div className="mt-1 text-[10px] text-zinc-500">
              {t("dashboard.motiv_longest")}: {motiv.longest_streak} · {t("dashboard.motiv_encourage")}
            </div>
          </div>
          <Link
            to="/app/motivasyon"
            className="group rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5 hover:border-purple-400"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-purple-700">
              <Sparkles className="h-4 w-4" /> {t("dashboard.motiv_panel_title")}
            </div>
            <div className="mt-1 text-sm text-zinc-700">
              {t("dashboard.motiv_panel_desc")}
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-purple-700">
              {t("dashboard.motiv_open")} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      )}

      {/* Super admin — Company overview */}
      {isSuperAdmin && (
        <div className="rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold text-zinc-900">
              {t("dashboard.co_summary")}
            </h2>
            <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
              {t("dashboard.co_super_admin")}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <MiniStat
              label={t("dashboard.co_active_users")}
              value={activeUserCount}
              suffix={`/ ${users.length}`}
            />
            <MiniStat label={t("dashboard.co_total_projects")} value={projects.length} />
            <MiniStat
              label={t("dashboard.co_total_tasks")}
              value={tasks.length}
            />
            <MiniStat
              label={t("dashboard.co_overdue")}
              value={
                tasks.filter(
                  (tk) =>
                    tk.status !== "done" &&
                    tk.due_at &&
                    new Date(tk.due_at).getTime() < now,
                ).length
              }
              tone="red"
            />
          </div>

          {companyGoal && (
            <div className="mt-6 rounded-lg bg-white p-4 ring-1 ring-blue-100">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  {t("dashboard.co_xp_goal")} ({companyGoal.period})
                </div>
                <div className="text-xs text-zinc-600">
                  <b className="text-amber-700">{companyGoal.current_xp}</b> /{" "}
                  {companyGoal.target_xp} XP
                </div>
              </div>
              <Progress
                value={Math.min(
                  100,
                  (companyGoal.current_xp / companyGoal.target_xp) * 100,
                )}
                className="h-2"
              />
              {companyGoal.top_contributors?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="text-[10px] font-semibold uppercase text-zinc-400">
                    {t("dashboard.co_contrib")}:
                  </span>
                  {companyGoal.top_contributors.map((c) => (
                    <span
                      key={c.user_id}
                      className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5"
                    >
                      {c.name}{" "}
                      <b className="text-amber-700">+{c.xp}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {leaderboard.length > 0 && (
            <div className="mt-4 rounded-lg bg-white p-4 ring-1 ring-blue-100">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                  <Users className="h-3.5 w-3.5" /> {t("dashboard.co_top5")}
                </div>
                <Link
                  to="/app/motivasyon"
                  className="text-[11px] font-medium text-blue-600 hover:underline"
                >
                  {t("dashboard.co_full_list")} →
                </Link>
              </div>
              <ul className="space-y-1">
                {leaderboard.slice(0, 5).map((row) => (
                  <li
                    key={row.user_id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-6 text-zinc-400">
                        {row.rank === 1 && "🥇"}
                        {row.rank === 2 && "🥈"}
                        {row.rank === 3 && "🥉"}
                        {row.rank > 3 && `#${row.rank}`}
                      </span>
                      <span className="text-zinc-800">{row.name}</span>
                    </span>
                    <span className="flex items-center gap-3 text-xs">
                      <span className="text-amber-700 font-semibold">
                        {row.xp} XP
                      </span>
                      <span className="text-zinc-500">
                        {row.tasks_completed} {t("dashboard.co_task_suffix")}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Charts row 1: trend + status */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title={t("dashboard.trend_title")}
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.trend}>
              <defs>
                <linearGradient id="doneGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
              <YAxis
                stroke="#71717A"
                fontSize={11}
                allowDecimals={false}
              />
              <Tooltip cursor={{ fill: "#f4f4f5" }} />
              <Area
                type="monotone"
                dataKey="done"
                stroke="#059669"
                fill="url(#doneGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.by_status")}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.byStatus}>
              <XAxis
                dataKey="name"
                stroke="#71717A"
                fontSize={11}
              />
              <YAxis
                stroke="#71717A"
                fontSize={11}
                allowDecimals={false}
              />
              <Tooltip cursor={{ fill: "#f4f4f5" }} />
              <Bar
                dataKey="value"
                fill="#18181B"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Burndown — 14 day open vs ideal + daily done bars. Uses a
          ComposedChart so we can overlay a red actual-open line, a
          zinc dashed ideal line and a green bar for tasks completed
          each day. */}
      <ChartCard
        title={t("dashboard.burndown_title")}
        testId="dashboard-burndown-chart"
      >
        <div className="mb-3 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-sm bg-red-500" />
            {t("dashboard.burndown_open")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-sm border border-dashed border-zinc-400" />
            {t("dashboard.burndown_ideal")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-sm bg-emerald-500" />
            {t("dashboard.burndown_done")}
          </span>
          {stats.burndown.length > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-zinc-600">
              {stats.burndown[stats.burndown.length - 1].open <=
              stats.burndown[stats.burndown.length - 1].ideal ? (
                <>
                  <TrendingDown className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-emerald-700">
                    {t("dashboard.burndown_on_track")}
                  </span>
                </>
              ) : (
                <>
                  <TrendingUp className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-amber-700">
                    {t("dashboard.burndown_behind")}
                  </span>
                </>
              )}
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={stats.burndown}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
            <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
            <YAxis
              stroke="#71717A"
              fontSize={11}
              allowDecimals={false}
            />
            <Tooltip cursor={{ fill: "#f4f4f5" }} />
            <Bar
              dataKey="done"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
              name={t("dashboard.burndown_done")}
              barSize={16}
            />
            <Line
              type="monotone"
              dataKey="ideal"
              stroke="#a1a1aa"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              dot={false}
              name={t("dashboard.burndown_ideal")}
            />
            <Line
              type="monotone"
              dataKey="open"
              stroke="#ef4444"
              strokeWidth={2.25}
              dot={{ r: 2 }}
              name={t("dashboard.burndown_open")}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Charts row 2: priority + by project */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title={t("dashboard.by_priority")}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={stats.byPriority}
                dataKey="value"
                nameKey="name"
                outerRadius={90}
                innerRadius={50}
              >
                {stats.byPriority.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={PRIORITY_COLORS[entry.priority] || "#a1a1aa"}
                  />
                ))}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.by_project")}>
          {stats.byProject.length === 0 ? (
            <p className="py-10 text-center text-sm text-zinc-400">
              {t("dashboard.no_open_tasks")}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.byProject} layout="vertical">
                <XAxis
                  type="number"
                  stroke="#71717A"
                  fontSize={11}
                  allowDecimals={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#71717A"
                  fontSize={11}
                  width={120}
                />
                <Tooltip cursor={{ fill: "#f4f4f5" }} />
                <Bar dataKey="open" fill="#0055FF" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Upcoming tasks + projects */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div
          data-testid="dashboard-upcoming"
          className="rounded-xl border border-zinc-200 bg-white p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
              {t("dashboard.upcoming")}
            </div>
            <Link
              to="/app/takvim"
              className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900"
            >
              {t("dashboard.upcoming_see_calendar")} →
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-400">
              {t("dashboard.no_upcoming")}
            </p>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((tk) => {
                const due = new Date(tk.due_at).getTime();
                const overdue = due < now && tk.status !== "done";
                return (
                  <li key={tk.id}>
                    <Link
                      to={`/app/projects/${tk.project_id}?task=${tk.id}`}
                      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-zinc-50"
                    >
                      <span
                        className={[
                          "inline-block h-2 w-2 rounded-full",
                          overdue
                            ? "bg-red-500"
                            : tk.priority === "high" || tk.priority === "urgent"
                              ? "bg-amber-500"
                              : "bg-blue-400",
                        ].join(" ")}
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                        {tk.title}
                      </span>
                      <span
                        className={[
                          "text-[11px]",
                          overdue ? "font-semibold text-red-600" : "text-zinc-500",
                        ].join(" ")}
                      >
                        {new Date(tk.due_at).toLocaleString(
                          i18n.language === "en" ? "en-US" : "tr-TR",
                          {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
              {t("dashboard.projects")}
            </div>
            <Link
              to="/app/projects"
              className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900"
            >
              {t("dashboard.all_projects")} →
            </Link>
          </div>
          {projects.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-400">
              {t("projects.empty")}
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {projects.slice(0, 6).map((p) => {
                const cnt = isMe ? p.open_for_me_count : p.open_count;
                const overdue = isMe ? p.has_overdue_for_me : p.has_overdue;
                return (
                  <li key={p.id}>
                    <Link
                      to={`/app/projects/${p.id}`}
                      className="flex items-center justify-between py-3 hover:bg-zinc-50 rounded-md px-2 -mx-2"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ background: p.color || "#0055FF" }}
                        />
                        <span className="text-sm font-medium text-zinc-900">
                          {p.name}
                        </span>
                        {overdue && (
                          <span className="text-red-500" title={t("dashboard.has_overdue_hint")}>
                            ⚡
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-zinc-500">
                        {cnt || 0} {t("dashboard.open_suffix")}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {!loaded && (
        <p className="py-6 text-center text-xs text-zinc-400">{t("common.loading")}</p>
      )}
    </div>
  );
}

function StatCard({ label, value, testId, highlight, icon, to }) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 truncate text-[10px] uppercase tracking-wider font-semibold text-zinc-500 sm:text-xs">
          {label}
        </div>
        <span className="flex-none">{icon}</span>
      </div>
      <div
        className={[
          "mt-1.5 font-display text-2xl font-semibold tracking-tight sm:mt-2 sm:text-3xl",
          highlight ? "text-red-600" : "text-zinc-950",
        ].join(" ")}
      >
        {value}
      </div>
    </>
  );
  // Clickable variant navigates to `to` (usually /app/projects with a
  // hash filter). Falls back to a plain div when `to` isn't provided so
  // dashboards that just want to render a stat can still use the card.
  const base = "block rounded-xl border border-zinc-200 bg-white p-3 transition-shadow sm:p-5";
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`${base} hover:shadow-md hover:border-zinc-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-400`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={`${base} hover:shadow-sm`}>
      {inner}
    </div>
  );
}

function MiniStat({ label, value, suffix, tone }) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-blue-100">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span
          className={[
            "font-display text-xl font-semibold",
            tone === "red" ? "text-red-600" : "text-zinc-900",
          ].join(" ")}
        >
          {value}
        </span>
        {suffix && (
          <span className="text-xs text-zinc-500">{suffix}</span>
        )}
      </div>
    </div>
  );
}

const TONE_CLASSES = {
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  purple: "border-purple-200 bg-purple-50 text-purple-800",
};

function QuickLink({ to, icon: Icon, label, count, countSuffix, tone, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={[
        "group flex items-center gap-3 rounded-xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md",
        TONE_CLASSES[tone] || TONE_CLASSES.blue,
      ].join(" ")}
    >
      <div className="rounded-lg bg-white/70 p-2">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider font-semibold opacity-80">
          {label}
        </div>
        {typeof count !== "undefined" && (
          <div className="text-sm font-semibold">
            {count} {countSuffix}
          </div>
        )}
      </div>
      <ArrowRight className="h-4 w-4 opacity-40 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

function ChartCard({ title, children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={[
        "rounded-xl border border-zinc-200 bg-white p-6",
        className,
      ].join(" ")}
    >
      <div className="mb-4 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
        {title}
      </div>
      {children}
    </div>
  );
}
